import React from "react";
import { useQueryClient } from "@tanstack/react-query";
import { format, addDays } from "date-fns";
import { ru } from "date-fns/locale";
import {
    VipGrantMode,
    VipSegmentRow,
    VipStateResponse,
    grantVip,
    revokeVipSegment,
    setStructuralAutopay,
    useUserVip,
} from "../api/users";
import { adminConfirm, adminToast } from "../lib/adminDialog";

interface Props {
    userId: number | null;
}

const PRESETS = [1, 7, 10] as const;

const formatDate = (value: string | null | undefined) => {
    if (!value) return "—";
    return format(new Date(value), "dd.MM.yyyy", { locale: ru });
};

const formatDateTime = (value: string | null | undefined) => {
    if (!value) return "—";
    return format(new Date(value), "dd.MM.yyyy HH:mm", { locale: ru });
};

const sourceLabel = (s: string) => {
    const map: Record<string, string> = {
        admin: "админ",
        shop: "оплата",
        gift: "подарок",
        promo: "акция",
        migration: "было раньше",
    };
    return map[s] ?? s;
};

/** Человекочитаемый источник: админка / Millennium / кабинет / турнир */
function describeOrigin(s: VipSegmentRow): {
    title: string;
    when: string;
    comment: string | null;
    packageLabel: string;
} {
    const meta = (s.meta ?? {}) as Record<string, unknown>;
    const actor = String(meta.actor ?? "");
    const via = String(meta.via ?? "");
    const slug = String(meta.slug ?? "").trim();
    const commentRaw = meta.comment;
    const comment =
        typeof commentRaw === "string" && commentRaw.trim()
            ? commentRaw.trim()
            : null;

    const slugTitle: Record<string, string> = {
        admin_grant: "Millennium · админ",
        admin_promocode: "Millennium · промокод",
        registration_trial: "Millennium · trial",
        gift_calendar: "Millennium · календарь",
        daily_mission: "Millennium · миссия",
        shop_reward: "Millennium · shop reward",
        auction: "Millennium · аукцион",
        lottery: "Millennium · лотерея",
        raffle: "Millennium · розыгрыш",
        tournament_prize: "Millennium · турнир",
        referral_reward: "Millennium · реферал",
        invite_link: "Millennium · инвайт",
        progress_transfer: "Millennium · перенос",
        shop: "Millennium · покупка",
        treasure: "Millennium · treasure",
        tribute: "Tribute",
        ambassador_promocode: "Millennium · амбассадор",
    };

    let title: string;
    if (s.label?.trim()) {
        title = s.label.trim();
    } else if (slug && slugTitle[slug]) {
        title = slugTitle[slug];
    } else if (s.source === "admin") {
        if (actor.startsWith("game_admin")) {
            title = "Millennium · админ";
        } else {
            title = "Админ";
        }
    } else if (s.source === "shop") {
        if (via.includes("structural") || via.includes("cabinet")) {
            title = "Кабинет";
        } else if (
            via.includes("buy_vip") ||
            via.includes("millennium") ||
            via.includes("game") ||
            via.includes("treasure")
        ) {
            title = "Millennium · покупка";
        } else if (via.includes("tribute")) {
            title = "Tribute";
        } else if (via.includes("auto") || via.includes("cron")) {
            title = "Автооплата";
        } else {
            title = "Оплата";
        }
    } else if (s.source === "gift") {
        title = actor.startsWith("hub_admin")
            ? "Админ · подарок"
            : "Millennium · подарок";
    } else if (s.source === "promo") {
        title = "Акция";
    } else if (s.source === "migration") {
        title = "Перенос";
    } else {
        title = sourceLabel(s.source);
    }

    const packageLabel = s.is_forever
        ? "бессрочно"
        : s.sku
          ? String(s.sku)
          : `${s.duration_days ?? s.remaining_days ?? "?"} дн.`;

    return {
        title,
        when: formatDate(s.created_at),
        comment: comment && comment !== slug ? comment : slug || comment,
        packageLabel,
    };
}

const statusLabel = (s: string) => {
    const map: Record<string, string> = {
        queued: "в очереди",
        active: "активно",
        frozen: "на паузе",
        exhausted: "закончился",
        revoked: "отменён",
    };
    return map[s] ?? s;
};

const statusClass = (s: string) => {
    const map: Record<string, string> = {
        queued: "text-amber-700 dark:text-amber-400",
        active: "text-emerald-700 dark:text-emerald-400 font-medium",
        frozen: "text-sky-700 dark:text-sky-400",
        exhausted: "text-gray-400",
        revoked: "text-red-600 dark:text-red-400",
    };
    return map[s] ?? "";
};

const canRevoke = (s: VipSegmentRow) =>
    ["queued", "active", "frozen"].includes(s.status);

/** FIFO: для queued показать «до какой даты дотянет VIP», не прочерк */
type UntilParts = {
    prefix: string;
    date: string;
    time: string | null;
    statusHint: string;
};

function enrichSegments(
    segments: VipSegmentRow[],
): Array<VipSegmentRow & UntilParts> {
    const alive = ["queued", "active", "frozen"];
    const ordered = [...segments].sort((a, b) => {
        const ta = a.created_at ? new Date(a.created_at).getTime() : a.id;
        const tb = b.created_at ? new Date(b.created_at).getTime() : b.id;
        return ta - tb || a.id - b.id;
    });

    let cursor = new Date();
    const byId = new Map<number, UntilParts>();

    const fromIso = (iso: string, prefix = "", hint = ""): UntilParts => ({
        prefix,
        date: format(new Date(iso), "dd.MM.yyyy", { locale: ru }),
        time: format(new Date(iso), "HH:mm", { locale: ru }),
        statusHint: hint,
    });

    for (const s of ordered) {
        if (!alive.includes(s.status)) {
            byId.set(
                s.id,
                s.expires_at
                    ? fromIso(s.expires_at, "", statusLabel(s.status))
                    : {
                          prefix: "",
                          date: "—",
                          time: null,
                          statusHint: statusLabel(s.status),
                      },
            );
            continue;
        }
        if (s.is_forever) {
            byId.set(s.id, {
                prefix: "",
                date: "∞",
                time: null,
                statusHint: "бессрочно",
            });
            continue;
        }
        if (s.status === "active" && s.expires_at) {
            const end = new Date(s.expires_at);
            if (end.getTime() <= Date.now()) {
                byId.set(
                    s.id,
                    fromIso(s.expires_at, "", statusLabel("exhausted")),
                );
            } else {
                byId.set(s.id, fromIso(s.expires_at, "", "сейчас идёт"));
                cursor = end.getTime() > cursor.getTime() ? end : cursor;
            }
            continue;
        }
        if (s.status === "frozen") {
            const days = s.remaining_days ?? s.duration_days ?? 0;
            byId.set(s.id, {
                prefix: "",
                date: days ? `+${days} дн.` : "пауза",
                time: null,
                statusHint: "на паузе",
            });
            continue;
        }
        const days = Math.max(0, s.remaining_days ?? s.duration_days ?? 0);
        const start = cursor.getTime() > Date.now() ? cursor : new Date();
        const end = addDays(start, days);
        byId.set(
            s.id,
            fromIso(
                end.toISOString(),
                "→ ",
                `после ${formatDateTime(start.toISOString())}`,
            ),
        );
        cursor = end;
    }

    return segments.map((s) => {
        const pastActive =
            s.status === "active" &&
            !!s.expires_at &&
            new Date(s.expires_at).getTime() <= Date.now();
        const status = pastActive ? "exhausted" : s.status;
        const u = byId.get(s.id) ?? {
            prefix: "",
            date: "—",
            time: null,
            statusHint: statusLabel(status),
        };
        return { ...s, status, ...u };
    });
}

const previewAfterDays = (data: VipStateResponse, days: number): string => {
    if (data.vip_forever) return "бессрочно (дни на паузе)";
    const base =
        data.vip && data.vip_expires_at
            ? new Date(data.vip_expires_at)
            : new Date();
    const start = base.getTime() > Date.now() ? base : new Date();
    return formatDateTime(addDays(start, days).toISOString());
};

/**
 * VIP ledger UI (ТЗ §11): статус, выдача, история, revoke.
 */
export const VipSection: React.FC<Props> = ({ userId }) => {
    const queryClient = useQueryClient();
    const { data, isLoading, error } = useUserVip(userId);

    const [mode, setMode] = React.useState<VipGrantMode>("days");
    const [days, setDays] = React.useState<number>(7);
    const [expiresOn, setExpiresOn] = React.useState("");
    const [comment, setComment] = React.useState("");
    const [isSaving, setIsSaving] = React.useState(false);
    const [actionError, setActionError] = React.useState<string | null>(null);

    const refresh = () => {
        queryClient.invalidateQueries({ queryKey: ["user-vip", userId] });
        queryClient.invalidateQueries({ queryKey: ["user-full-info", userId] });
    };

    const run = async (
        action: () => Promise<VipStateResponse>,
        successMsg?: string,
    ) => {
        if (!userId) return;
        setIsSaving(true);
        setActionError(null);
        try {
            const result = await action();
            refresh();
            if (result.game_sync?.pending) {
                adminToast(
                    "VIP в Хабе уже действует. Синхронизация с игрой — повтор позже.",
                    "warning",
                );
            } else {
                adminToast(
                    successMsg ??
                        (result.vip_forever
                            ? "VIP выдан: бессрочно"
                            : `VIP до ${formatDateTime(result.vip_expires_at)}`),
                    "success",
                );
            }
            setComment("");
        } catch (e: any) {
            const msg =
                e?.response?.data?.message ||
                e?.message ||
                "Не удалось выполнить действие";
            setActionError(msg);
            adminToast(msg, "error");
        } finally {
            setIsSaving(false);
        }
    };

    const onGrant = async () => {
        if (!userId || !data) return;
        if (mode === "until" && !expiresOn) {
            setActionError("Укажите дату окончания");
            return;
        }
        if (mode === "days" && (!days || days < 1)) {
            setActionError("Укажите число дней ≥ 1");
            return;
        }

        let message: string;
        if (mode === "forever") {
            message =
                "Выдать бессрочный VIP?\nТекущие дни сохранятся на паузе.";
        } else if (mode === "until") {
            message = `Выдать VIP до ${expiresOn} (конец дня Asia/Tbilisi)?\nЖивые сегменты с днями будут отозваны (superseded).`;
        } else {
            const until = previewAfterDays(data, days);
            message = `Выдать +${days} дн.?\nVIP станет примерно до: ${until}`;
        }

        const ok = await adminConfirm(message, {
            title: "Выдача VIP",
            confirmText: "Выдать",
            cancelText: "Отмена",
        });
        if (!ok) return;

        await run(() =>
            grantVip(userId, {
                mode,
                days: mode === "days" ? days : undefined,
                expires_on: mode === "until" ? expiresOn : undefined,
                source: "admin",
                comment: comment.trim() || undefined,
            }),
        );
    };

    const onRevoke = async (seg: VipSegmentRow) => {
        if (!userId || !data) return;
        const ok = await adminConfirm(
            `Отменить выдачу #${seg.id} (${describeOrigin(seg).title})?\nОстальные периоды останутся, дата пересчитается.`,
            {
                title: "Отмена VIP-периода",
                confirmText: "Отменить",
                cancelText: "Назад",
                danger: true,
            },
        );
        if (!ok) return;
        await run(() => revokeVipSegment(userId, seg.id, "admin_revoke"));
    };

    const onToggleAutopay = async () => {
        if (!userId || !data || typeof data.structural_autopay !== "boolean")
            return;
        const next = !data.structural_autopay;
        const ok = await adminConfirm(
            next
                ? "Включить автосписание structural VIP с баланса?"
                : "Выключить автосписание structural VIP с баланса?",
            {
                title: "Автосписание",
                confirmText: next ? "Включить" : "Выключить",
                cancelText: "Отмена",
                danger: !next,
            },
        );
        if (!ok) return;
        await run(
            () => setStructuralAutopay(userId, next),
            next
                ? "Автосписание включено"
                : "Автосписание выключено",
        );
    };

    if (!userId) return null;

    if (isLoading) {
        return (
            <div className="flex-1 rounded-xl bg-violet-50/80 dark:bg-violet-950/30 border border-violet-200/80 dark:border-violet-900/40 p-3.5 text-sm text-gray-500">
                Загрузка VIP…
            </div>
        );
    }

    if (error || !data) {
        return (
            <div className="flex-1 rounded-xl border border-red-200 bg-red-50 p-3.5 text-sm text-red-600">
                Не удалось загрузить VIP
            </div>
        );
    }

    const segments = enrichSegments(data.segments ?? []);
    const chip = mode === "days" ? `${days}д` : mode === "until" ? "дата" : "∞";
    const isPresetDays =
        mode === "days" && PRESETS.includes(days as (typeof PRESETS)[number]);

    const dayChip = (active: boolean) =>
        `h-8 min-w-[2.5rem] px-2.5 rounded-lg text-xs font-semibold transition border ${
            active
                ? "bg-violet-700 text-white border-violet-700 shadow-sm"
                : "bg-violet-100 text-violet-900 border-violet-300 hover:bg-violet-200 dark:bg-violet-950/60 dark:text-violet-100 dark:border-violet-700"
        }`;

    const altChip = (active: boolean, tone: "sky" | "indigo") =>
        `h-8 px-2.5 rounded-lg text-xs font-semibold transition border ${
            active
                ? tone === "sky"
                    ? "bg-sky-600 text-white border-sky-600 shadow-sm"
                    : "bg-indigo-700 text-white border-indigo-700 shadow-sm"
                : tone === "sky"
                  ? "bg-sky-50 text-sky-900 border-sky-300 hover:bg-sky-100 dark:bg-sky-950/40 dark:text-sky-100 dark:border-sky-700"
                  : "bg-indigo-50 text-indigo-900 border-indigo-300 hover:bg-indigo-100 dark:bg-indigo-950/40 dark:text-indigo-100 dark:border-indigo-700"
        }`;

    return (
        <div className="flex-1 flex flex-col rounded-xl border border-violet-200/80 dark:border-violet-800/60 bg-gradient-to-br from-violet-50 to-indigo-50/70 dark:from-violet-950/40 dark:to-indigo-950/30 p-3.5 gap-2.5 shadow-sm min-h-0">
            <div className="flex items-center gap-2 flex-wrap min-w-0">
                <h4 className="text-sm font-semibold text-violet-900 dark:text-violet-100">
                    VIP-статус
                </h4>
                <span
                    className={`shrink-0 rounded-full px-2.5 h-6 inline-flex items-center gap-1 text-[11px] font-medium ${
                        data.vip
                            ? "bg-emerald-100 text-emerald-800 dark:bg-emerald-900/50 dark:text-emerald-200"
                            : "bg-white/80 text-gray-600 dark:bg-gray-800 dark:text-gray-300"
                    }`}
                >
                    {data.vip ? (
                        data.vip_forever ? (
                            "активен · бессрочно"
                        ) : data.vip_expires_at ? (
                            <>
                                активен до{" "}
                                <span>
                                    {format(
                                        new Date(data.vip_expires_at),
                                        "dd.MM.yyyy",
                                        { locale: ru },
                                    )}
                                </span>
                                <span className="text-emerald-600/80 dark:text-emerald-300/90 font-normal">
                                    {format(
                                        new Date(data.vip_expires_at),
                                        "HH:mm",
                                        { locale: ru },
                                    )}
                                </span>
                            </>
                        ) : (
                            "активен"
                        )
                    ) : (
                        "не активен"
                    )}
                </span>
                {typeof data.structural_autopay === "boolean" && (
                    <button
                        type="button"
                        role="switch"
                        aria-checked={data.structural_autopay}
                        disabled={isSaving}
                        onClick={() => void onToggleAutopay()}
                        className={`ml-auto shrink-0 h-6 pl-2 pr-1 rounded-full inline-flex items-center gap-1.5 text-[11px] font-medium whitespace-nowrap border transition disabled:opacity-50 ${
                            data.structural_autopay
                                ? "bg-violet-100 text-violet-900 border-violet-300 hover:bg-violet-200/80 dark:bg-violet-900/40 dark:text-violet-100 dark:border-violet-700"
                                : "bg-white/90 text-gray-600 border-violet-200 hover:bg-violet-50 dark:bg-gray-900/50 dark:text-gray-300 dark:border-violet-800"
                        }`}
                        title="Автосписание structural VIP с баланса (не Stripe). Клик — вкл/выкл"
                    >
                        <span className="opacity-80">автосписание</span>
                        <span
                            className={
                                data.structural_autopay
                                    ? "font-semibold text-violet-700 dark:text-violet-200"
                                    : "font-semibold text-gray-500"
                            }
                        >
                            {data.structural_autopay ? "вкл" : "выкл"}
                        </span>
                        <span
                            aria-hidden
                            className={`relative inline-flex h-3.5 w-6 shrink-0 rounded-full transition-colors ${
                                data.structural_autopay
                                    ? "bg-violet-600"
                                    : "bg-gray-300 dark:bg-gray-600"
                            }`}
                        >
                            <span
                                className={`absolute top-[2px] left-[2px] h-2.5 w-2.5 rounded-full bg-white shadow transition-transform ${
                                    data.structural_autopay
                                        ? "translate-x-2.5"
                                        : "translate-x-0"
                                }`}
                            />
                        </span>
                    </button>
                )}
            </div>

            {/* Выдача */}
            <div className="rounded-xl bg-white dark:bg-gray-900/60 border border-violet-200/90 dark:border-violet-800/60 p-3 space-y-2.5 shadow-sm">
                <div className="text-[10px] font-semibold uppercase tracking-wide text-violet-700 dark:text-violet-300">
                    Выдать VIP
                </div>

                <div className="flex flex-wrap items-center gap-2">
                    <span className="text-[11px] font-medium text-violet-500 dark:text-violet-400 shrink-0">
                        Срок
                    </span>
                    {PRESETS.map((d) => (
                        <button
                            key={d}
                            type="button"
                            disabled={isSaving}
                            onClick={() => {
                                setMode("days");
                                setDays(d);
                            }}
                            className={dayChip(isPresetDays && days === d)}
                        >
                            {d}д
                        </button>
                    ))}
                    <label
                        className={`inline-flex items-center gap-1 h-8 pl-2 pr-1.5 rounded-lg border text-xs font-semibold ${
                            mode === "days" && !isPresetDays
                                ? "bg-violet-700 text-white border-violet-700"
                                : "bg-violet-100 text-violet-900 border-violet-300 dark:bg-violet-950/60 dark:text-violet-100 dark:border-violet-700"
                        }`}
                        title="Любое число дней"
                    >
                        <input
                            type="number"
                            min={1}
                            max={3650}
                            value={days}
                            disabled={isSaving}
                            onFocus={() => setMode("days")}
                            onChange={(e) => {
                                setMode("days");
                                setDays(
                                    Math.max(1, Number(e.target.value) || 1),
                                );
                            }}
                            className="w-12 h-6 rounded border-0 bg-white/90 dark:bg-gray-800 px-1 text-sm font-semibold text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-1 focus:ring-violet-400"
                        />
                        <span
                            className={
                                mode === "days" && !isPresetDays
                                    ? "text-violet-100 pr-0.5"
                                    : "text-violet-700 dark:text-violet-200 pr-0.5"
                            }
                        >
                            дн.
                        </span>
                    </label>

                    <span className="w-px h-5 bg-violet-200 dark:bg-violet-800 mx-0.5 hidden sm:block" />

                    <button
                        type="button"
                        disabled={isSaving}
                        onClick={() => setMode("until")}
                        className={altChip(mode === "until", "sky")}
                    >
                        до даты
                    </button>
                    <button
                        type="button"
                        disabled={isSaving}
                        onClick={() => setMode("forever")}
                        className={altChip(mode === "forever", "indigo")}
                    >
                        бессрочно ∞
                    </button>

                    {mode === "until" && (
                        <input
                            type="date"
                            value={expiresOn}
                            onChange={(e) => setExpiresOn(e.target.value)}
                            disabled={isSaving}
                            className="h-9 rounded-lg border border-sky-300 dark:border-sky-700 bg-sky-50 dark:bg-gray-800 px-2 text-sm"
                        />
                    )}
                </div>

                <div className="flex flex-wrap items-center gap-6 pt-0.5">
                    <button
                        type="button"
                        disabled={isSaving}
                        onClick={() => void onGrant()}
                        className="h-9 shrink-0 inline-flex items-center gap-2 rounded-lg bg-gradient-to-r from-violet-700 to-indigo-600 hover:from-violet-600 hover:to-indigo-500 px-4 text-sm font-semibold text-white disabled:opacity-50 shadow-md shadow-violet-600/30 transition"
                    >
                        {isSaving ? (
                            "…"
                        ) : (
                            <>
                                Выдать
                                <span className="font-bold tracking-wide text-amber-200">
                                    {chip}
                                </span>
                            </>
                        )}
                    </button>
                    <input
                        type="text"
                        value={comment}
                        onChange={(e) => setComment(e.target.value)}
                        disabled={isSaving}
                        placeholder="Комментарий — необязательно"
                        className="w-56 h-9 rounded-lg border border-dashed border-violet-300 dark:border-violet-700 bg-violet-50/50 dark:bg-violet-950/20 px-2.5 text-sm text-gray-700 dark:text-gray-200 placeholder:text-violet-400 focus:outline-none focus:ring-2 focus:ring-violet-300/60"
                    />
                </div>
            </div>

            {/* История */}
            <div className="flex-1 min-h-0 rounded-xl bg-white/70 dark:bg-gray-900/40 border border-violet-100/80 dark:border-violet-900/40 p-2.5 flex flex-col">
                <div className="text-[10px] font-semibold uppercase tracking-wide text-violet-700/80 dark:text-violet-300 mb-1.5">
                    История выдач
                </div>
                {segments.length === 0 ? (
                    <div className="text-xs text-gray-500 dark:text-gray-400 py-2">
                        Пока ничего не выдавали
                    </div>
                ) : (
                    <div className="overflow-x-auto overflow-y-auto max-h-64 flex-1">
                        <table className="min-w-full text-xs">
                            <thead>
                                <tr className="text-left text-gray-500 dark:text-gray-400">
                                    <th className="pr-2 pb-1.5 font-medium whitespace-nowrap">
                                        дата
                                    </th>
                                    <th className="pr-2 pb-1.5 font-medium">
                                        откуда
                                    </th>
                                    <th className="pr-2 pb-1.5 font-medium whitespace-nowrap">
                                        пакет
                                    </th>
                                    <th className="pr-2 pb-1.5 font-medium">
                                        коммент
                                    </th>
                                    <th className="pr-2 pb-1.5 font-medium">
                                        статус
                                    </th>
                                    <th className="pr-2 pb-1.5 font-medium whitespace-nowrap">
                                        VIP до
                                    </th>
                                    <th className="pb-1.5" />
                                </tr>
                            </thead>
                            <tbody>
                                {segments.map((s) => {
                                    const origin = describeOrigin(s);
                                    return (
                                        <tr
                                            key={s.id}
                                            className="border-t border-violet-100/60 dark:border-violet-900/40 text-gray-900 dark:text-gray-100"
                                        >
                                            <td className="pr-2 py-1.5 align-middle whitespace-nowrap text-gray-600">
                                                {origin.when}
                                            </td>
                                            <td className="pr-2 py-1.5 align-middle max-w-[130px]">
                                                <span
                                                    className="line-clamp-2 leading-snug"
                                                    title={origin.title}
                                                >
                                                    {origin.title}
                                                </span>
                                            </td>
                                            <td className="pr-2 py-1.5 align-middle whitespace-nowrap text-violet-700 dark:text-violet-300 font-medium">
                                                {origin.packageLabel}
                                            </td>
                                            <td className="pr-2 py-1.5 align-middle max-w-[140px]">
                                                {origin.comment ? (
                                                    <span
                                                        className="text-gray-500 italic line-clamp-2 leading-snug"
                                                        title={origin.comment}
                                                    >
                                                        {origin.comment}
                                                    </span>
                                                ) : (
                                                    <span className="text-gray-300">
                                                        —
                                                    </span>
                                                )}
                                            </td>
                                            <td className="pr-2 py-1.5 align-middle">
                                                <div
                                                    className={statusClass(
                                                        s.status,
                                                    )}
                                                >
                                                    {statusLabel(s.status)}
                                                </div>
                                                {s.status === "queued" && (
                                                    <div className="text-[10px] text-amber-600/80 leading-tight">
                                                        {s.statusHint}
                                                    </div>
                                                )}
                                            </td>
                                            <td className="pr-2 py-1.5 whitespace-nowrap align-middle font-medium">
                                                <span className="text-gray-800 dark:text-gray-100">
                                                    {s.prefix}
                                                    {s.date}
                                                </span>
                                                {s.time && (
                                                    <span className="ml-1 text-violet-500 dark:text-violet-300 font-normal">
                                                        {s.time}
                                                    </span>
                                                )}
                                            </td>
                                            <td className="py-1.5 text-right align-middle">
                                                {canRevoke(s) && (
                                                    <button
                                                        type="button"
                                                        disabled={isSaving}
                                                        onClick={() =>
                                                            void onRevoke(s)
                                                        }
                                                        className="h-7 px-2 rounded-md text-red-600 hover:bg-red-50 dark:hover:bg-red-950/40 font-medium disabled:opacity-50"
                                                    >
                                                        Отменить
                                                    </button>
                                                )}
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>

            {actionError && (
                <div className="text-xs text-red-600 dark:text-red-400">
                    {actionError}
                </div>
            )}
        </div>
    );
};

/** @deprecated */
